import type { useTranslation } from "react-i18next";
import { formatDate } from "../utils/dateFormatters";
import { formatWeekdays } from "../utils/formatWeekdays";
import type { BookingCodeRule } from "../api/contracts/booking-code";

/**
 * Render a single booking-code rule into a human-readable, localized string.
 * Every `BookingCodeRule` variant maps to one `booking_code.rules.<variant>`
 * i18n template. Open-ended date ranges (null `from`/`to`) fall back to the
 * one-sided templates so we never print a blank bound.
 */
export const renderBookingCodeRule = (
  rule: BookingCodeRule,
  t: ReturnType<typeof useTranslation>["t"],
): string => {
  switch (rule.type) {
    case "bookingPeriod":
    case "stayPeriod": {
      const base = `booking_code.rules.${rule.type}`;
      const from = rule.from ? formatDate(rule.from) : null;
      const to = rule.to ? formatDate(rule.to) : null;
      if (from && to) return t(`${base}.range`, { from, to });
      if (from) return t(`${base}.from`, { from });
      if (to) return t(`${base}.to`, { to });
      return "";
    }
    case "blackout":
      return t("booking_code.rules.blackout", {
        from: formatDate(rule.from),
        to: formatDate(rule.to),
      });
    case "minNights":
      return t("booking_code.rules.minNights", { count: rule.nights });
    case "maxNights":
      return t("booking_code.rules.maxNights", { count: rule.nights });
    case "earliestDaysBefore":
      return t("booking_code.rules.earliestDaysBefore", { count: rule.days });
    case "latestDaysBefore":
      return t("booking_code.rules.latestDaysBefore", { count: rule.days });
    case "stayWeekdays":
      return t("booking_code.rules.stayWeekdays", {
        weekdays: formatWeekdays(rule.weekdays, t),
      });
    case "arrivalWeekdays":
      return t("booking_code.rules.arrivalWeekdays", {
        weekdays: formatWeekdays(rule.weekdays, t),
      });
    case "category":
      return t("booking_code.rules.category", {
        names: rule.names.join(", "),
      });
    case "maxRoomsPerNight":
      return t("booking_code.rules.maxRoomsPerNight", { count: rule.rooms });
    case "discountScope": {
      // Composed from whichever parts the price-code rule actually sets, so one
      // line reads "29 Jun 2026 – 13 Aug 2026, Fri–Sun, Double room". Segment
      // order is fixed: when → which days → which unit → how many.
      const segments: string[] = [];
      const from = rule.from ? formatDate(rule.from) : null;
      const to = rule.to ? formatDate(rule.to) : null;
      if (from && to) {
        segments.push(t("booking_code.rules.scope.period.range", { from, to }));
      } else if (from) {
        segments.push(t("booking_code.rules.scope.period.from", { from }));
      } else if (to) {
        segments.push(t("booking_code.rules.scope.period.to", { to }));
      }
      if (rule.weekdays && rule.weekdays.length > 0) {
        segments.push(formatWeekdays(rule.weekdays, t));
      }
      if (rule.categoryName) segments.push(rule.categoryName);
      if (rule.maxRoomsPerNight != null) {
        segments.push(
          t("booking_code.rules.scope.maxRooms", { count: rule.maxRoomsPerNight }),
        );
      }
      return segments.filter((s) => s.length > 0).join(", ");
    }
    default:
      return "";
  }
};

/**
 * Display grouping for the rules popover. The backend emits rules as a flat
 * list in field order, so we regroup into a small, fixed set of sections that
 * read top-down (when to book → when the code is valid → how long → how many
 * rooms → which categories → where the discount lands) instead of an arbitrary
 * mix. Each group keeps the listed type order; identical rendered lines are
 * de-duplicated within a group.
 *
 * `discount` is deliberately last and separate: every group above it decides
 * whether the code applies at all, while `discountScope` rules only say which
 * dates and units the price actually drops on. Mixing the two put two
 * identical bare date ranges side by side with no way to tell them apart.
 */
const RULE_GROUPS: { key: string; types: BookingCodeRule["type"][] }[] = [
  { key: "booking", types: ["bookingPeriod", "earliestDaysBefore", "latestDaysBefore"] },
  { key: "stay", types: ["stayPeriod", "blackout", "arrivalWeekdays", "stayWeekdays"] },
  { key: "length", types: ["minNights", "maxNights"] },
  { key: "rooms", types: ["maxRoomsPerNight"] },
  { key: "categories", types: ["category"] },
  { key: "discount", types: ["discountScope"] },
];

export interface RenderedRuleGroup {
  key: string;
  title: string;
  lines: string[];
}

/**
 * Turn a flat `BookingCodeRule[]` into ordered, de-duplicated, localized
 * groups for display. The length group merges a min+max pair into a single
 * "X–Y nights" line; when only one bound is set it falls back to the plain
 * min/max line. Groups with no lines are dropped.
 */
export const groupBookingCodeRules = (
  rules: BookingCodeRule[],
  t: ReturnType<typeof useTranslation>["t"],
): RenderedRuleGroup[] => {
  const groups: RenderedRuleGroup[] = [];

  for (const group of RULE_GROUPS) {
    let lines: string[];

    if (group.key === "length") {
      const min = rules.find(
        (r): r is Extract<BookingCodeRule, { type: "minNights" }> =>
          r.type === "minNights",
      );
      const max = rules.find(
        (r): r is Extract<BookingCodeRule, { type: "maxNights" }> =>
          r.type === "maxNights",
      );
      if (min && max) {
        lines = [
          t("booking_code.rules.nightsRange", {
            min: min.nights,
            max: max.nights,
          }),
        ];
      } else {
        lines = [];
        if (min) lines.push(renderBookingCodeRule(min, t));
        if (max) lines.push(renderBookingCodeRule(max, t));
      }
    } else {
      // Keep the group's declared type order, preserving source order (site
      // before price code) for repeated kinds.
      const inGroup = group.types.flatMap((type) =>
        rules.filter((r) => r.type === type),
      );
      lines = inGroup
        .map((r) => renderBookingCodeRule(r, t))
        .filter((line) => line.length > 0);
    }

    const deduped = [...new Set(lines)];
    if (deduped.length > 0) {
      groups.push({
        key: group.key,
        title: t(`booking_code.rules.groups.${group.key}`),
        lines: deduped,
      });
    }
  }

  return groups;
};
